import React, { useState, createContext, useContext, useCallback, useRef, useEffect } from 'react'
import { Typography, Box } from '@mui/material'
import PropTypes from 'prop-types'
import Modal from 'components/design-system/Modal'
import Button from 'components/design-system/Button'

const ConfirmDialogContext = createContext(null)

export const useConfirmDialog = () => useContext(ConfirmDialogContext)

export const ConfirmDialogProvider = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [dialogContent, setDialogContent] = useState('')
  const [dialogTitle, setDialogTitle] = useState('')
  const [confirmText, setConfirmText] = useState('Confirm')
  const [cancelText, setCancelText] = useState('Cancel')

  const [resolver, setResolver] = useState(null)
  const cancelButtonRef = useRef(null)

  const handleConfirm = () => {
    setOpen(false)
    if (resolver) {
      resolver(true)
      setResolver(null)
    }
  }

  const handleCancel = () => {
    setOpen(false)
    if (resolver) {
      resolver(false)
      setResolver(null)
    }
  }

  const openDialog = useCallback(({ content, title, confirmText = 'Confirm', cancelText = 'Cancel' }) => {
    setDialogContent(content)
    setDialogTitle(title)
    setConfirmText(confirmText)
    setCancelText(cancelText)
    setOpen(true)

    return new Promise((resolve) => {
      setResolver(() => resolve)
    })
  }, [])

  useEffect(() => {
    if (open && cancelButtonRef.current) {
      cancelButtonRef.current.focus()
    }
  }, [open])

  return (
    <ConfirmDialogContext.Provider value={{ openDialog }}>
      {children}
      <Modal open={open} onClose={handleCancel} title={dialogTitle.toUpperCase()} width={420} disableEnforceFocus>
        {typeof dialogContent === 'string' ? (
          <Typography
            sx={{
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-primary)',
              fontSize: 'var(--font-size-14)',
              fontWeight: 'var(--font-weight-regular)',
              lineHeight: 1.3
            }}
          >
            {dialogContent}
          </Typography>
        ) : (
          dialogContent
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-12)' }}>
          <Button variant="secondary" ref={cancelButtonRef} onClick={handleCancel}>
            {cancelText}
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            {confirmText}
          </Button>
        </Box>
      </Modal>
    </ConfirmDialogContext.Provider>
  )
}

ConfirmDialogProvider.propTypes = {
  children: PropTypes.node
}
